import type { TableRow } from './shared';
import {
  Article,
  Callout,
  Clause,
  DataTable,
  EmailLink,
  PageShell,
  SubItem,
  SubTitle
} from './shared';

function Badge({
  children,
  variant
}: {
  children: React.ReactNode;
  variant: 'green' | 'red';
}) {
  const styles = {
    green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-800'
  };
  return (
    <span
      className={`inline-block text-xs font-bold px-2.5 py-0.5 rounded-full ml-1.5 align-middle ${styles[variant]}`}
    >
      {children}
    </span>
  );
}

export default function RefundPolicy() {
  const tocItems = [
    { href: '#s0', label: '기본 안내' },
    { href: '#s1', label: '1. 단일 클래스 취소·환불 규정' },
    { href: '#s2', label: '2. 전액 환불 사유' },
    { href: '#s3', label: '3. 프로젝트(포트폴리오) 클래스 취소·환불 규정' },
    { href: '#s4', label: '4. 할인쿠폰 복구 규정' },
    { href: '#s5', label: '5. 수업 중 부적절한 태도에 대한 제재조치' },
    { href: '#s6', label: '6. 서비스 이용을 저해하는 행위에 대한 제재조치' }
  ];

  const s1Headers = ['취소 신청 시점', '환불 금액'];
  const s1Rows: TableRow[] = [
    {
      '취소 신청 시점': (
        <span>
          수업 전날(자정)까지 <Badge variant="green">수수료 없음</Badge>
        </span>
      ),
      '환불 금액': (
        <span>
          결제 금액 <strong>전액 환불</strong>
        </span>
      )
    },
    {
      '취소 신청 시점': (
        <span>
          수업 당일 이후 <Badge variant="red">환불 불가</Badge>
        </span>
      ),
      '환불 금액': '취소 및 환불 불가'
    }
  ];

  const s3Headers = ['취소 신청 시점', '환불 내용'];
  const s3Rows: TableRow[] = [
    {
      '취소 신청 시점': (
        <span>
          다음 클래스 당일 이전까지 <Badge variant="green">수수료 없음</Badge>
        </span>
      ),
      '환불 내용': (
        <span>
          남은 전체 일정 취소, 별도 수수료 없이 <strong>전액 환불</strong>
        </span>
      )
    },
    {
      '취소 신청 시점': '클래스 진행 중 취소',
      '환불 내용': (
        <span>
          남은 일정 환불 + <strong>별도 할인 쿠폰 지급</strong>
          <br />
          <span className="text-[12.5px] text-slate-400">
            쿠폰 금액 및 조건은 회사 정책에 따름
          </span>
        </span>
      )
    },
    {
      '취소 신청 시점': (
        <span>
          클래스 종료 후 <Badge variant="red">환불 불가</Badge>
        </span>
      ),
      '환불 내용': '취소 및 환불 불가'
    }
  ];

  return (
    <PageShell
      title="라이브클래스 수강 취소·환불 및 제재조치 규정"
      announcedAt="2026-04-08"
      effectiveAt="2026-05-08"
      intro="라이브클래스의 취소·환불 및 제재조치 규정은 수강생과 선생님 모두가 안정적인 수업 환경을 누릴 수 있도록 마련된 기준입니다. 수강 신청 전 반드시 확인해 주시기 바라며, 내용을 숙지하지 못해 발생하는 불이익에 대해 회사는 별도의 책임을 지지 않습니다."
      tocItems={tocItems}
    >
      {/* ── 기본 안내 ── */}
      <Article id="s0" num="기본" title="기본 안내">
        <Clause num="•">
          회사가 무상으로 제공하는 부가 서비스(수업 알림, 무료 체험 수업 등)는
          회원에게 별도의 권리를 발생시키지 않으며, 이와 관련한 보상 의무도
          없습니다.
        </Clause>
        <Clause num="•">
          수강생 본인의 사정으로 발생한 서비스 이용 장애(인터넷 연결 불량, 기기
          문제, 기능 미숙지 등)에 대해 회사는 책임을 지지 않습니다.
        </Clause>
        <Clause num="•">
          모든 취소·환불은{' '}
          <a
            href="https://event-us.kr"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline font-medium"
          >
            이벤터스(event-us.kr)
          </a>{' '}
          사이트를 통해서만 처리됩니다. 취소·환불 세부 정책은{' '}
          <a
            href="https://eventwithus.notion.site/12020cee82378038a0d6dfb568cc4b7c"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline font-medium"
          >
            이벤터스 환불 정책
          </a>
          을 따릅니다.
        </Clause>
        <Clause num="•">
          수업 스케줄의 변경은 불가합니다. 일정 변경이 필요한 경우 기존 신청을
          취소하고 원하는 일정으로 새로 신청해야 합니다.
        </Clause>
        <Clause num="•">
          이벤트·사은품 등 혜택을 받은 이후 취소·환불하는 경우, 해당 혜택도 함께
          취소된 것으로 간주합니다.
        </Clause>
      </Article>

      {/* ── Section 1 ── */}
      <Article id="s1" num="1" title="단일 클래스 취소·환불 규정">
        <Callout variant="green">
          라이브클래스는 <strong>구매취소 수수료를 부과하지 않습니다.</strong>{' '}
          수업 전날까지 취소를 신청하면 결제 금액 전액을 환불받을 수 있습니다.
        </Callout>
        <DataTable headers={s1Headers} rows={s1Rows} />
        <Callout variant="yellow">
          수업이 시작된 이후에는 어떠한 사유로도 취소 및 환불이 불가합니다. 단,
          회사 또는 선생님의 귀책사유로 수업이 정상 진행되지 못한 경우에는 결제
          금액 전액이 환불됩니다.
        </Callout>
      </Article>

      {/* ── Section 2 ── */}
      <Article id="s2" num="2" title="전액 환불 사유">
        <p className="text-sm text-slate-700 leading-relaxed mb-3">
          취소 신청 시점과 관계없이 아래 사유에 해당하는 경우 전액 환불됩니다.
        </p>
        <SubItem>회사의 귀책사유로 서비스 장애가 발생한 경우</SubItem>
        <SubItem>선생님의 귀책사유로 수업이 정상 진행되지 못한 경우</SubItem>
        <SubItem>
          선생님이 수업 시작 전 해당 수강생의 참여가 어렵다고 판단하여 회사에
          취소를 요청한 경우 (권장 연령 불일치, 학습 수준 불일치, 과거 수업 참여
          이력상 어려움이 있었던 경우 등)
        </SubItem>
        <SubItem>
          폭풍·홍수·지진 등 자연재해로 인해 생물 활동에 심각한 영향이 발생한
          경우 (단, 뉴스·사진 등으로 객관적 확인이 가능해야 하며, 감염병은
          해당하지 않음)
        </SubItem>
      </Article>

      {/* ── Section 3 ── */}
      <Article
        id="s3"
        num="3"
        title="프로젝트(포트폴리오) 클래스 취소·환불 규정"
      >
        <p className="text-sm text-slate-700 leading-relaxed mb-3">
          여러 차시로 구성된 프로젝트(포트폴리오) 클래스는 단일 클래스와 별도의
          규정이 적용됩니다.
        </p>
        <SubTitle>취소 방식</SubTitle>
        <Clause num="•">
          개별 차시만 선택하여 취소하는 것은 불가하며, 취소 신청 시점 이후 남은
          전체 일정이 일괄 취소됩니다.
        </Clause>
        <Clause num="•">
          이미 진행 완료된 차시는 환불 대상에서 제외됩니다.
        </Clause>
        <SubTitle>환불 기준</SubTitle>
        <DataTable headers={s3Headers} rows={s3Rows} />
        <Callout>
          클래스 진행 중 취소 시 제공되는 할인 쿠폰의 세부 조건(유효기간, 사용
          범위 등)은 취소 처리 시 별도 안내합니다.
        </Callout>
      </Article>

      {/* ── Section 4 ── */}
      <Article id="s4" num="4" title="할인쿠폰 복구 규정">
        <Clause num="•">
          <strong>수업 전날까지 취소하는 경우</strong>: 사용한 할인쿠폰은 원래의
          유효기간 그대로 복구됩니다.
        </Clause>
        <Clause num="•">
          <strong>선생님·회사 귀책으로 취소되는 경우</strong>: 사용한 할인쿠폰은
          원래 사용 기간만큼 연장된 유효기간으로 복구됩니다.
        </Clause>
        <Clause num="•">
          <strong>환불 불가 시점 이후 취소</strong>: 쿠폰이 복구되지 않으며,
          차액 환불 또는 쿠폰 추가 지급도 진행되지 않습니다.
        </Clause>
      </Article>

      {/* ── Section 5 ── */}
      <Article id="s5" num="5" title="수업 중 부적절한 태도에 대한 제재조치">
        <p className="text-sm text-slate-700 leading-relaxed mb-3">
          라이브클래스는 모든 수강생이 안전하고 쾌적하게 수업에 참여할 수 있도록
          수업 전·중·후 모니터링을 운영합니다. 수강생의 부적절한 수업 태도가
          확인된 경우, 선생님 의견·수업 기록 등을 종합적으로 검토하여 제재조치를
          시행할 수 있습니다.
        </p>
        <SubTitle>부적절한 수업 태도 예시</SubTitle>
        <SubItem>
          소음이 심한 장소(카페, 차량, 이동 중 등)에서 수업에 참여하는 경우
        </SubItem>
        <SubItem>
          수업 권장 연령 또는 난이도와 맞지 않아 정상 참여가 어려운 경우
        </SubItem>
        <SubItem>수업에 필요한 교재·자료·프로그램이 준비되지 않은 경우</SubItem>
        <SubItem>
          수업 중 자리를 반복적으로 이탈하거나 다른 활동(식사 등)을 하는 경우
        </SubItem>
        <SubItem>
          신청하지 않은 타인(형제자매, 친구 등)을 함께 참여시키는 경우
        </SubItem>
        <SubItem>
          다른 수강생이나 선생님에게 폭언·비방·허위사실을 유포하거나 채팅 도배
          등으로 수업을 방해하는 경우
        </SubItem>
        <SubTitle>시행 가능한 제재조치</SubTitle>
        <SubItem>음소거·채팅 제한 등 수업 내 관리 조치</SubItem>
        <SubItem>소그룹·대기실 기능을 활용한 분리 조치</SubItem>
        <SubItem>수업 강퇴 처리 (이 경우 전액 환불)</SubItem>
        <SubItem>수강 후기 작성 제한 또는 삭제</SubItem>
        <SubItem>해당 선생님 수업 재신청 불가</SubItem>
        <Callout variant="yellow">
          제재조치가 내려졌다는 이유만으로 환불을 요청할 수 없습니다. 단, 수업
          진행 중 선생님의 판단 아래 강퇴 처리된 경우에는 전액 환불됩니다.
        </Callout>
      </Article>

      {/* ── Section 6 ── */}
      <Article
        id="s6"
        num="6"
        title="서비스 이용을 저해하는 행위에 대한 제재조치"
      >
        <p className="text-sm text-slate-700 leading-relaxed mb-3">
          라이브클래스 서비스 전반의 원활한 운영을 방해하는 행위가 확인된 경우,
          회사는 귀책 여부를 판단한 뒤 제재조치를 시행할 수 있습니다.
        </p>
        <SubTitle>해당 행위 예시</SubTitle>
        <SubItem>결제 후 취소 없이 무단 결석을 반복하는 경우</SubItem>
        <SubItem>수강하지 않은 수업의 후기를 임의로 작성한 경우</SubItem>
        <SubItem>
          쿠폰·이벤트를 비정상적인 방법으로 악용하여 금전적 이익을 취한 경우
        </SubItem>
        <SubItem>
          수강생·보호자·선생님·고객센터를 대상으로 폭언·비방·허위사실을 유포한
          경우
        </SubItem>
        <SubItem>스케줄을 정당한 이유 없이 반복적으로 취소하는 경우</SubItem>
        <SubTitle>시행 가능한 제재조치</SubTitle>
        <SubItem>후기 작성 제한 또는 삭제</SubItem>
        <SubItem>이벤트·쿠폰 지급 대상 제외</SubItem>
        <SubItem>채팅 기능 이용 제한</SubItem>
        <SubItem>특정 선생님 수업 신청 불가</SubItem>
        <SubItem>서비스 이용 일시 정지 또는 영구 제한</SubItem>
        <Callout>
          위에 명시된 사유 외에도, 회사는 개별 상황에 따라 중재자로서 필요한
          제재조치를 시행할 수 있습니다.
        </Callout>
      </Article>

      {/* Addendum */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-7 py-5 mb-5">
        <h2 className="text-[15px] font-bold text-blue-900 mb-3">부칙</h2>
        <div className="flex flex-col gap-2 text-[14px] text-slate-700">
          <div className="flex gap-2">
            <span>1.</span>
            <span>본 규정은 2026년 05월 08일부터 적용됩니다.</span>
          </div>
          <div className="flex gap-2">
            <span>2.</span>
            <span>
              결제·취소·환불의 실제 처리는 이벤터스 정책을 따르며, 세부 환불
              정책은 아래 링크에서 확인할 수 있습니다.
            </span>
          </div>
          <div className="flex gap-2">
            <span>3.</span>
            <span>
              문의: <EmailLink email="gotoaiknow@gmail.com" />
            </span>
          </div>
        </div>
      </div>

      {/* Footer links */}
      <div className="bg-white border border-slate-200 rounded-xl px-7 py-6">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
          관련 링크
        </h3>
        <div className="flex flex-wrap gap-3">
          {[
            {
              emoji: '🔗',
              label: '이벤터스 수강 신청 바로가기',
              href: 'https://event-us.kr'
            },
            {
              emoji: '📄',
              label: '이벤터스 환불 정책 확인하기',
              href: 'https://eventwithus.notion.site/12020cee82378038a0d6dfb568cc4b7c'
            },
            {
              emoji: '✉️',
              label: '고객센터 문의',
              href: 'mailto:gotoaiknow@gmail.com'
            }
          ].map((link) => (
            <a
              key={link.href}
              href={link.href}
              target={link.href.startsWith('mailto') ? undefined : '_blank'}
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-sm font-medium px-4 py-2 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors no-underline"
            >
              <span>{link.emoji}</span>
              <span>{link.label}</span>
            </a>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
